// Estado "ya vi este onboarding" del usuario, persistido en la DB para que sea
// cross-device (vale en cualquier navegador/PC, no solo donde se mostró).
//
// Fuente de verdad: columna `profiles.onboarding_seen` (jsonb), un objeto del
// tipo { "welcome": true, "ctx_dialog": true, ... }. localStorage queda como
// ESPEJO/fallback rápido en el mismo navegador: nunca pisa a la DB, solo cubre
// el rato en que la DB todavía no cargó o si falla la conexión.
//
// `profiles` hoy NO tiene RLS (ver memoria de seguridad), así que el update
// desde el cliente funciona igual que `home_message_seen`. Si en el futuro se
// activa RLS, hace falta una política de UPDATE de la propia fila.

import { supabase } from "./supabase";

// Llaves de los onboardings, tal como viven dentro del jsonb.
export type OnboardingKey =
  | "welcome" // modal de bienvenida (pantalla de proyectos)
  | "tour" // tour guiado principal del editor
  | "ctx_transitions"
  | "ctx_dialog"
  | "ctx_video"
  | "ctx_audio"
  | "ctx_files";

// Llave legacy de localStorage por cada onboarding (compat con lo ya guardado).
const LEGACY_LS: Record<OnboardingKey, string> = {
  welcome: "editor_welcome_seen",
  tour: "editor_onboarding_seen",
  ctx_transitions: "ctx_transitions_seen",
  ctx_dialog: "ctx_dialog_seen",
  ctx_video: "ctx_video_seen",
  ctx_audio: "ctx_audio_seen",
  ctx_files: "ctx_files_seen",
};

// Cache en memoria del jsonb + a qué usuario corresponde.
let cache: Record<string, boolean> | null = null;
let cachedUserId: string | null = null;

function lsGet(legacyKey: string): boolean {
  try {
    return !!localStorage.getItem(legacyKey);
  } catch {
    return false;
  }
}

function lsSet(legacyKey: string): void {
  try {
    localStorage.setItem(legacyKey, "1");
  } catch {
    /* sin localStorage → noop */
  }
}

/**
 * Carga `onboarding_seen` del usuario UNA vez y lo cachea. Idempotente: si ya
 * está cacheado para ese usuario, no vuelve a pegarle a la DB. Llamar temprano
 * (al montar el editor / la pantalla de proyectos) para que `isSeen` tenga la
 * verdad de la DB antes de decidir si muestra algo.
 */
export async function loadOnboardingSeen(userId: string): Promise<void> {
  if (cache && cachedUserId === userId) return;
  cachedUserId = userId;
  try {
    const { data, error } = await supabase
      .from("profiles")
      .select("onboarding_seen")
      .eq("id", userId)
      .single();
    if (error) {
      cache = {};
      return;
    }
    const raw = (data as { onboarding_seen?: unknown } | null)?.onboarding_seen;
    cache =
      raw && typeof raw === "object"
        ? { ...(raw as Record<string, boolean>) }
        : {};
  } catch {
    cache = {};
  }
}

/**
 * ¿El usuario ya vio este onboarding? La DB manda; si todavía no cargó (cache
 * null) caemos al localStorage de este navegador. Sincrónica a propósito: los
 * gates de los tours son sincrónicos.
 */
export function isSeen(key: OnboardingKey): boolean {
  if (cache) return cache[key] === true || lsGet(LEGACY_LS[key]);
  return lsGet(LEGACY_LS[key]);
}

/**
 * Marca un onboarding como visto: localStorage (espejo) + cache + DB. El write
 * a la DB es read-modify-write (trae el jsonb actual, mergea la llave y lo
 * guarda) para NO pisar otras llaves; es fire-and-forget — si falla, el
 * localStorage ya cubre el caso en este navegador. Si no hay userId, persiste
 * solo en localStorage.
 */
export function markSeen(key: OnboardingKey, userId: string | null): void {
  lsSet(LEGACY_LS[key]);
  if (!cache) cache = {};
  cache[key] = true;
  if (!userId) return;
  void (async () => {
    try {
      const { data } = await supabase
        .from("profiles")
        .select("onboarding_seen")
        .eq("id", userId)
        .single();
      const current = (data as { onboarding_seen?: unknown } | null)
        ?.onboarding_seen;
      const merged: Record<string, boolean> = {
        ...(current && typeof current === "object"
          ? (current as Record<string, boolean>)
          : {}),
        [key]: true,
      };
      cache = merged;
      await supabase
        .from("profiles")
        .update({ onboarding_seen: merged })
        .eq("id", userId);
    } catch {
      /* localStorage ya quedó seteado como fallback */
    }
  })();
}
